var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import dayjs from 'dayjs';
import { useGetCustomerApi, useGetMealPrepPlanApi, useGetPersonApi, } from '../../hooks';
import { LoaderType } from '../../hooks/api/utils';
import { useLanguage } from '../../locale';
import { useStorage } from '../../contexts';
import { mealPrepDoneStore } from './mealPrepDoneStore';
var buildItemKey = function (date, mealType, recipeId, actionId) { return "".concat(date, "_").concat(mealType, "_").concat(recipeId, "_").concat(actionId); };
export var useMealPrepPlanFacade = function (loaderType, enabled) {
    if (loaderType === void 0) { loaderType = LoaderType.Contextual; }
    if (enabled === void 0) { enabled = true; }
    var _a = useLanguage(), t = _a[0], locale = _a[2];
    var storage = useStorage();
    var customer = useGetCustomerApi(true, loaderType).data;
    var personLeader = useGetPersonApi(customer === null || customer === void 0 ? void 0 : customer.personId, loaderType).data;
    var _b = useGetMealPrepPlanApi(personLeader === null || personLeader === void 0 ? void 0 : personLeader.familyId, loaderType, enabled), mealPrepPlan = _b.data, isLoading = _b.isLoading, isFetching = _b.isFetching, refetch = _b.refetch;
    var doneMap = React.useSyncExternalStore(mealPrepDoneStore.subscribe, mealPrepDoneStore.getDoneMap, mealPrepDoneStore.getDoneMap);
    var isDoneMapLoaded = React.useSyncExternalStore(mealPrepDoneStore.subscribe, mealPrepDoneStore.isLoaded, mealPrepDoneStore.isLoaded);
    // Carica lo stato "fatto" persistito sul device
    React.useEffect(function () {
        void mealPrepDoneStore.hydrate(storage);
    }, [storage]);
    var persistDoneMap = React.useCallback(function (nextMap) {
        mealPrepDoneStore.setDoneMap(storage, nextMap);
    }, [storage]);
    var items = React.useMemo(function () {
        if (!(mealPrepPlan === null || mealPrepPlan === void 0 ? void 0 : mealPrepPlan.items)) {
            return [];
        }
        var result = [];
        mealPrepPlan.items.forEach(function (day) {
            day.meals.forEach(function (meal) {
                meal.actions.forEach(function (action) {
                    var key = buildItemKey(day.date, meal.type, meal.recipeId, action.id);
                    result.push({
                        key: key,
                        actionId: action.id,
                        text: action.text,
                        category: action.category,
                        recipeId: meal.recipeId,
                        recipeName: meal.recipeName,
                        mealType: meal.type,
                        date: day.date,
                        done: !!doneMap[key],
                    });
                });
            });
        });
        return result;
    }, [mealPrepPlan, doneMap]);
    // Al ricalcolo del piano le occorrenze non più presenti vengono rimosse
    // dallo storage, così lo stato "fatto" non cresce all'infinito
    React.useEffect(function () {
        if (!isDoneMapLoaded || !(mealPrepPlan === null || mealPrepPlan === void 0 ? void 0 : mealPrepPlan.items)) {
            return;
        }
        var validKeys = new Set(items.map(function (item) { return item.key; }));
        var staleKeys = Object.keys(doneMap).filter(function (key) { return !validKeys.has(key); });
        if (staleKeys.length > 0) {
            var nextMap_1 = __assign({}, doneMap);
            staleKeys.forEach(function (key) { return delete nextMap_1[key]; });
            persistDoneMap(nextMap_1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDoneMapLoaded, mealPrepPlan, items]);
    // Header di sezione
    var formatSectionTitle = React.useCallback(function (date) {
        var day = dayjs(date);
        var dateFormat = locale === 'us' ? 'dddd, MMMM D' : 'dddd D MMMM';
        var label = day.format(dateFormat);
        var capitalized = label.charAt(0).toUpperCase() + label.slice(1);
        if (day.isSame(dayjs(), 'day')) {
            return "".concat(t('mealPrepPage.today'), ", ").concat(capitalized);
        }
        if (day.isSame(dayjs().add(1, 'day'), 'day')) {
            return "".concat(t('mealPrepPage.tomorrow'), ", ").concat(capitalized);
        }
        return capitalized;
    }, [locale, t]);
    var sections = React.useMemo(function () {
        var byDate = new Map();
        items.forEach(function (item) {
            var section = byDate.get(item.date);
            if (!section) {
                section = {
                    id: item.date,
                    title: formatSectionTitle(item.date),
                    data: [],
                };
                byDate.set(item.date, section);
            }
            section.data.push(item);
        });
        return Array.from(byDate.values());
    }, [items, formatSectionTitle]);
    var markAsDone = React.useCallback(function (key) {
        var _a;
        persistDoneMap(__assign(__assign({}, doneMap), (_a = {}, _a[key] = true, _a)));
    }, [doneMap, persistDoneMap]);
    var restoreAsToDo = React.useCallback(function (key) {
        var nextMap = __assign({}, doneMap);
        delete nextMap[key];
        persistDoneMap(nextMap);
    }, [doneMap, persistDoneMap]);
    var hasPendingActions = React.useMemo(function () { return items.some(function (item) { return !item.done; }); }, [items]);
    var getMealLabel = React.useCallback(function (mealType) { return t("mealPrepPage.meals.".concat(mealType)); }, [t]);
    return {
        sections: sections,
        items: items,
        hasPendingActions: hasPendingActions,
        isLoading: isLoading || !isDoneMapLoaded,
        isFetching: isFetching,
        markAsDone: markAsDone,
        restoreAsToDo: restoreAsToDo,
        getMealLabel: getMealLabel,
        refetch: refetch,
    };
};
